import React, { useState, useEffect } from "react";
import { getPageVisibility, savePageVisibility } from "../firebase";
import "./PageSettings.css";

interface PageVisibility {
  hero: boolean;
  invitation: boolean;
  gallery: boolean;
  location: boolean;
  guestbook: boolean;
}

const PageSettings: React.FC = () => {
  const [visibility, setVisibility] = useState<PageVisibility>({
    hero: true,
    invitation: true,
    gallery: true,
    location: true,
    guestbook: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPageVisibility();
  }, []);

  const loadPageVisibility = async () => {
    try {
      setLoading(true);
      const data = await getPageVisibility();
      setVisibility(data);
    } catch (error) {
      console.error("Error loading page visibility:", error);
      alert("페이지 설정을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (page: keyof PageVisibility) => {
    setVisibility((prev) => ({
      ...prev,
      [page]: !prev[page],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await savePageVisibility(visibility);
      alert("페이지 설정이 저장되었습니다.");
    } catch (error) {
      console.error("Error saving page visibility:", error);
      alert("페이지 설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("모든 페이지를 표시하도록 초기화하시겠습니까?")) {
      setVisibility({
        hero: true,
        invitation: true,
        gallery: true,
        location: true,
        guestbook: true,
      });
    }
  };

  const pageInfo = [
    {
      key: "hero" as keyof PageVisibility,
      name: "히어로 섹션",
      description: "메인 배경과 신랑신부 이름",
      icon: "👰🤵",
    },
    {
      key: "invitation" as keyof PageVisibility,
      name: "초대장",
      description: "초대 메시지와 부제목",
      icon: "💌",
    },
    {
      key: "gallery" as keyof PageVisibility,
      name: "갤러리",
      description: "사진 갤러리",
      icon: "🖼️",
    },
    {
      key: "location" as keyof PageVisibility,
      name: "오시는 길",
      description: "예식장 위치와 계좌번호",
      icon: "📍",
    },
    {
      key: "guestbook" as keyof PageVisibility,
      name: "축하한마디",
      description: "방명록과 축하 메시지",
      icon: "💬",
    },
  ];

  if (loading) {
    return (
      <div className="page-settings loading">페이지 설정을 불러오는 중...</div>
    );
  }

  return (
    <div className="page-settings">
      <h2>페이지 표시 설정</h2>
      <p className="description">
        웹사이트에서 표시할 페이지를 선택하세요. 숨겨진 페이지는 방문자에게
        보이지 않습니다.
      </p>

      <div className="settings-grid">
        {pageInfo.map((page) => (
          <div
            key={page.key}
            className={`setting-card ${
              visibility[page.key] ? "active" : "inactive"
            }`}
          >
            <div className="setting-header">
              <div className="page-icon">{page.icon}</div>
              <div className="page-info">
                <h3>{page.name}</h3>
                <p>{page.description}</p>
              </div>
              <div className="toggle-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={visibility[page.key]}
                    onChange={() => handleToggle(page.key)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="setting-status">
              <span
                className={`status-badge ${
                  visibility[page.key] ? "visible" : "hidden"
                }`}
              >
                {visibility[page.key] ? "표시됨" : "숨겨짐"}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="settings-actions">
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "저장 중..." : "설정 저장"}
        </button>
        <button className="reset-btn" onClick={handleReset} disabled={saving}>
          초기화
        </button>
      </div>

      <div className="preview-info">
        <h3>미리보기</h3>
        <p>현재 설정으로는 다음 페이지들이 표시됩니다:</p>
        <div className="preview-list">
          {pageInfo
            .filter((page) => visibility[page.key])
            .map((page) => (
              <span key={page.key} className="preview-item">
                {page.icon} {page.name}
              </span>
            ))}
        </div>
        {pageInfo.filter((page) => !visibility[page.key]).length > 0 && (
          <div className="hidden-pages">
            <p>숨겨진 페이지:</p>
            <div className="preview-list">
              {pageInfo
                .filter((page) => !visibility[page.key])
                .map((page) => (
                  <span key={page.key} className="preview-item hidden">
                    {page.icon} {page.name}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSettings;
